import React from 'react';
import { joinAriaIds, useWxbFieldIds } from '../_internal/fieldA11y';
import './Input.css';

export interface WxbInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'prefix'> {
  label?: string;
  error?: string | boolean;
  helpText?: string;
  prefix?: React.ReactNode;
}

export const WxbInput = React.forwardRef<HTMLInputElement, WxbInputProps>(
  ({
    label,
    error,
    helpText,
    className = '',
    disabled,
    prefix,
    id,
    'aria-describedby': ariaDescribedBy,
    'aria-invalid': ariaInvalid,
    ...props
  }, ref) => {
    const isError = !!error;
    const { controlId, feedbackId } = useWxbFieldIds(id);
    const hasFeedback = typeof error === 'string' || Boolean(helpText);
    const describedBy = joinAriaIds(ariaDescribedBy, hasFeedback ? feedbackId : undefined);
    const inputClass = `wxb-input ${isError ? 'wxb-input-error' : ''} ${disabled ? 'wxb-input-disabled' : ''} ${className}`;
    const controlClass = `wxb-input-control ${isError ? 'wxb-input-control-error' : ''} ${disabled ? 'wxb-input-control-disabled' : ''}`;

    const renderHelpText = () => {
      if (typeof error === 'string') {
        return <span id={feedbackId} className="wxb-help wxb-help-error" role="alert">{error}</span>;
      }
      if (helpText) {
        return <span id={feedbackId} className="wxb-help">{helpText}</span>;
      }
      return null;
    };

    return (
      <div className="wxb-field">
        {label && <label className="wxb-label" htmlFor={controlId}>{label}</label>}
        {prefix ? (
          <div className={controlClass}>
            <span className="wxb-input-prefix">{prefix}</span>
            <input
              ref={ref}
              id={controlId}
              className={inputClass}
              disabled={disabled}
              aria-describedby={describedBy}
              aria-invalid={ariaInvalid ?? (isError || undefined)}
              {...props}
            />
          </div>
        ) : (
          <input
            ref={ref}
            id={controlId}
            className={inputClass}
            disabled={disabled}
            aria-describedby={describedBy}
            aria-invalid={ariaInvalid ?? (isError || undefined)}
            {...props}
          />
        )}
        {renderHelpText()}
      </div>
    );
  }
);

WxbInput.displayName = 'WxbInput';
